import { useCallback, useEffect, useRef, useState } from "react";
import { GetTenantOverview, TenantOverview } from "./CoreAPI";

/** Snapshots are collected every 5 minutes; polling faster returns identical rows. */
export const OVERVIEW_POLL_MS = 5 * 60 * 1000;

/** "idle" means no token yet — nothing has been attempted. */
export type OverviewState = "idle" | "loading" | "loaded" | "forbidden" | "unauthorised" | "failed";

export interface iUseTenantOverview {
    state: OverviewState;
    /** The last successful overview, retained across a later failure. */
    overview?: TenantOverview;
    reload: () => Promise<void>;
}

/**
 * Maps a non-loaded response onto a state.
 *
 * `isOk` is threaded through separately from `status` because the call site's
 * `response.isOk && response.data` guard excludes only the successful-with-body
 * case — a 200/204 with an absent body reaches this function too, with
 * `isOk: true`. That must not be read as an empty overview: absent is not
 * zero, so an OK response with no body maps to "failed", same as a malformed
 * response.
 */
function stateForStatus(status: number, isOk: boolean): OverviewState {
    if (isOk) {
        return "failed";
    }
    if (status === 403) {
        return "forbidden";
    }
    if (status === 401) {
        return "unauthorised";
    }
    return "failed";
}

/**
 * Fetches the cross-tenant overview and polls it.
 *
 * Distinguishes forbidden, unauthorised and failed rather than collapsing them
 * into an empty table. CallEmsApi leaves `data` undefined for 401/403/404 with
 * no body, so without this mapping all three would render as "no tenants" —
 * which an operator cannot tell apart from "all tenants are quiet".
 *
 * On a failed poll the previous overview is retained. Stale figures stamped
 * with their own asOf are more useful than none.
 *
 * Not built on src/hooks/useInterval.tsx: that hook ticks internally every
 * 1000ms regardless of the requested interval and assigns to a ref during
 * render, neither of which suits a 5-minute poll driven from an effect.
 */
export function useTenantOverview(tenantToken: string | undefined): iUseTenantOverview {
    const [state, setState] = useState<OverviewState>(tenantToken ? "loading" : "idle");
    const [overview, setOverview] = useState<TenantOverview | undefined>(undefined);

    // Identifies the most recently started request. A response is applied
    // only if it is still the newest one in flight when it resolves. Note
    // that a setState after unmount is a harmless no-op in React 18+, so this
    // guard is not here to prevent a leak — it exists to stop an older
    // response from overwriting a newer one (e.g. two Retry clicks in quick
    // succession). A plain "cancelled" boolean cannot do that job, and also
    // breaks under React.StrictMode (see src/index.tsx): its dev-mode
    // mount -> cleanup -> mount re-run would un-cancel the first mount's
    // still-in-flight request.
    const requestId = useRef(0);

    const load = useCallback(async () => {
        if (!tenantToken) {
            return;
        }

        const thisRequest = ++requestId.current;
        const response = await GetTenantOverview(tenantToken);
        if (thisRequest !== requestId.current) {
            return;
        }

        if (response.isOk && response.data) {
            setOverview(response.data);
            setState("loaded");
            return;
        }

        setState(stateForStatus(response.status, response.isOk));
    }, [tenantToken]);

    useEffect(() => {
        if (!tenantToken) {
            setState("idle");
            return;
        }

        setState("loading");
        void load();

        const timer = setInterval(() => {
            void load();
        }, OVERVIEW_POLL_MS);

        return () => {
            clearInterval(timer);
        };
    }, [tenantToken, load]);

    return { state, overview, reload: load };
}
